import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { collection, query, orderBy, limit, getDocs, where, doc, getDoc, setDoc, deleteDoc, addDoc, serverTimestamp, startAfter } from 'firebase/firestore';
import { db, auth } from '../firebase/config';
import { Heart, MessageSquare, ChevronRight, X, Send, Plus, ChevronUp, Layers, Megaphone } from 'lucide-react';
import LabPortfolioPosts from './lab/LabPortfolioPosts';

/**
 * 보철 갤러리 — 인스타그램 스타일
 * - 상단: 스폰서 슬롯 (gallerySponsors, '광고' 라벨 — 현재 무료 운영, 추후 유료화)
 * - 카테고리 칩 필터 + 정사각 그리드 + 무한 스크롤 (24개씩)
 * - 상세 모달: 사진 슬라이드 / 좋아요 / 댓글 / 기공소 프로필 이동
 */
const CATEGORIES = [
    '전체', '작업물', '지르코니아', '임플란트', 'PFM', '라미네이트',
    '틀니', '인레이/온레이', '교정장치', '크라운/브릿지', 'CAD/CAM'
];
const PAGE_SIZE = 24;

function LabFeed({ user }) {
    const navigate = useNavigate();
    const [posts, setPosts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [loadingMore, setLoadingMore] = useState(false);
    const [hasMore, setHasMore] = useState(true);
    const [category, setCategory] = useState('전체');
    const [sponsors, setSponsors] = useState([]);
    const [likedPosts, setLikedPosts] = useState(new Set());
    const [showMyPosts, setShowMyPosts] = useState(false);

    const lastDocRef = useRef(null);
    const profileCacheRef = useRef({});   // labId → { profileImage, name }
    const sentinelRef = useRef(null);
    const loadingRef = useRef(false);

    // 기공소 대표인지 확인 (직원=companyId 있음, 치과/외주 제외)
    const bt = user?.businessType || '';
    const isClinic = bt === 'clinic' || bt === 'dental' || bt === '치과';
    const isOutsource = bt === 'outsource';
    const isStaff = !!user?.companyId;
    const isLabOwner = user && !isClinic && !isOutsource && !isStaff;
    const isSubscribed = user?.isSubscribed === true;

    // Detail modal
    const [selectedPost, setSelectedPost] = useState(null);
    const [comments, setComments] = useState([]);
    const [commentText, setCommentText] = useState('');
    const [currentPhotoIdx, setCurrentPhotoIdx] = useState(0);

    /* ── 기공소 프로필 캐시 로드 (페이지 단위) ── */
    const enrichPosts = useCallback(async (list) => {
        const cache = profileCacheRef.current;
        const missing = [...new Set(list.map(p => p.labId))].filter(id => id && !cache[id]);
        await Promise.all(missing.map(async lid => {
            try {
                const adDoc = await getDoc(doc(db, 'labAdvertisements', lid));
                cache[lid] = adDoc.exists()
                    ? { profileImage: adDoc.data().profileImageUrl || adDoc.data().profileImage || '', name: adDoc.data().businessName || '' }
                    : { profileImage: '', name: '' };
            } catch (_) { cache[lid] = { profileImage: '', name: '' }; }
        }));
        return list.map(p => ({
            ...p,
            labProfileImage: cache[p.labId]?.profileImage || '',
            labName: p.labName || cache[p.labId]?.name || '',
        }));
    }, []);

    /* ── 게시물 페이지 로드 (무한 스크롤) ── */
    const fetchPage = useCallback(async (cat, isFirst) => {
        if (loadingRef.current) return;
        loadingRef.current = true;
        isFirst ? setLoading(true) : setLoadingMore(true);
        try {
            const constraints = [];
            if (cat !== '전체') constraints.push(where('category', '==', cat));
            constraints.push(orderBy('createdAt', 'desc'));
            if (!isFirst && lastDocRef.current) constraints.push(startAfter(lastDocRef.current));
            constraints.push(limit(PAGE_SIZE));

            const snap = await getDocs(query(collection(db, 'labPosts'), ...constraints));
            lastDocRef.current = snap.docs[snap.docs.length - 1] || lastDocRef.current;
            setHasMore(snap.docs.length === PAGE_SIZE);

            const page = await enrichPosts(snap.docs.map(d => ({ id: d.id, ...d.data() })));
            setPosts(prev => isFirst ? page : [...prev, ...page]);
        } catch (e) {
            console.error('갤러리 로드 실패:', e);
        } finally {
            loadingRef.current = false;
            setLoading(false);
            setLoadingMore(false);
        }
    }, [enrichPosts]);

    // 카테고리 변경 → 처음부터 다시
    useEffect(() => {
        lastDocRef.current = null;
        setHasMore(true);
        fetchPage(category, true);
    }, [category, fetchPage]);

    // 스폰서 + 내 좋아요 (1회)
    useEffect(() => {
        (async () => {
            try {
                const snap = await getDocs(query(collection(db, 'gallerySponsors'), where('active', '==', true)));
                setSponsors(snap.docs.map(d => ({ id: d.id, ...d.data() })).sort((a, b) => (a.order || 0) - (b.order || 0)));
            } catch (_) { /* 스폰서 없음 */ }
            if (auth.currentUser) {
                try {
                    const likesSnap = await getDocs(query(collection(db, 'labPostLikes'), where('userId', '==', auth.currentUser.uid)));
                    setLikedPosts(new Set(likesSnap.docs.map(d => d.data().postId)));
                } catch (_) { }
            }
        })();
    }, []);

    // 무한 스크롤 (하단 감지)
    useEffect(() => {
        const el = sentinelRef.current;
        if (!el) return;
        const observer = new IntersectionObserver((entries) => {
            if (entries[0].isIntersecting && hasMore && !loadingRef.current) {
                fetchPage(category, false);
            }
        }, { rootMargin: '400px' });
        observer.observe(el);
        return () => observer.disconnect();
    }, [category, hasMore, fetchPage]);

    const toggleLike = async (post, e) => {
        e?.stopPropagation();
        if (!auth.currentUser) { navigate('/login'); return; }
        const uid = auth.currentUser.uid;
        const likeId = `${uid}_${post.id}`;
        const likeRef = doc(db, 'labPostLikes', likeId);
        const postRef = doc(db, 'labPosts', post.id);

        try {
            if (likedPosts.has(post.id)) {
                await deleteDoc(likeRef);
                setLikedPosts(prev => { const s = new Set(prev); s.delete(post.id); return s; });
                setPosts(prev => prev.map(p => p.id === post.id ? { ...p, likes: Math.max(0, (p.likes || 1) - 1) } : p));
                if (selectedPost?.id === post.id) setSelectedPost(p => ({ ...p, likes: Math.max(0, (p.likes || 1) - 1) }));

                const { updateDoc } = await import('firebase/firestore');
                await updateDoc(postRef, { likes: Math.max(0, (post.likes || 1) - 1) });
            } else {
                await setDoc(likeRef, { userId: uid, postId: post.id, createdAt: serverTimestamp() });
                setLikedPosts(prev => new Set([...prev, post.id]));
                setPosts(prev => prev.map(p => p.id === post.id ? { ...p, likes: (p.likes || 0) + 1 } : p));
                if (selectedPost?.id === post.id) setSelectedPost(p => ({ ...p, likes: (p.likes || 0) + 1 }));

                const { updateDoc } = await import('firebase/firestore');
                await updateDoc(postRef, { likes: (post.likes || 0) + 1 });
            }
        } catch (e) {
            console.error('좋아요 처리 실패:', e);
        }
    };

    const openDetail = async (post) => {
        setCurrentPhotoIdx(0);
        let enrichedPost = { ...post };
        try {
            const labAdDoc = await getDoc(doc(db, 'labAdvertisements', post.labId));
            if (labAdDoc.exists()) {
                const ad = labAdDoc.data();
                enrichedPost.labProfileImage = ad.profileImageUrl || ad.profileImage || '';
                if (!enrichedPost.labName && ad.businessName) enrichedPost.labName = ad.businessName;
            }
            if (!enrichedPost.labName) {
                const userDoc = await getDoc(doc(db, 'users', post.labId));
                if (userDoc.exists()) {
                    enrichedPost.labName = userDoc.data().companyName || userDoc.data().businessName || userDoc.data().displayName || '';
                }
            }
        } catch (e) { console.log('기공소 정보 로드:', e); }
        setSelectedPost(enrichedPost);
        try {
            const q = query(
                collection(db, 'labPostComments'),
                where('postId', '==', post.id),
                orderBy('createdAt', 'desc')
            );
            const snap = await getDocs(q);
            setComments(snap.docs.map(d => ({ id: d.id, ...d.data() })));
        } catch (e) {
            setComments([]);
        }
    };

    const submitComment = async () => {
        if (!commentText.trim() || !auth.currentUser || !selectedPost) return;
        try {
            const commentData = {
                postId: selectedPost.id,
                userId: auth.currentUser.uid,
                userName: user?.companyName || user?.displayName || '사용자',
                text: commentText.trim(),
                createdAt: serverTimestamp(),
            };
            const docRef = await addDoc(collection(db, 'labPostComments'), commentData);
            setComments(prev => [{ id: docRef.id, ...commentData, createdAt: { toDate: () => new Date() } }, ...prev]);
            setCommentText('');

            const { updateDoc } = await import('firebase/firestore');
            await updateDoc(doc(db, 'labPosts', selectedPost.id), {
                commentCount: (selectedPost.commentCount || 0) + 1,
            });
            setSelectedPost(p => ({ ...p, commentCount: (p.commentCount || 0) + 1 }));
        } catch (e) {
            console.error('댓글 작성 실패:', e);
        }
    };

    const formatTime = (ts) => {
        if (!ts?.toDate) return '';
        const diff = Date.now() - ts.toDate().getTime();
        const mins = Math.floor(diff / 60000);
        if (mins < 60) return `${mins}분 전`;
        const hours = Math.floor(mins / 60);
        if (hours < 24) return `${hours}시간 전`;
        const days = Math.floor(hours / 24);
        return `${days}일 전`;
    };

    return (
        <div style={S.container}>
            {/* 페이지 헤더 */}
            <div style={S.pageHeader}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <div>
                        <h1 style={S.pageTitle}>보철 갤러리</h1>
                        <p style={S.pageSubtitle}>기공소들의 작업물을 둘러보세요</p>
                    </div>
                    {isLabOwner && isSubscribed && (
                        <button onClick={() => setShowMyPosts(!showMyPosts)} style={S.writeBtn}>
                            {showMyPosts ? <ChevronUp size={18} /> : <Plus size={18} />}
                            {showMyPosts ? '접기' : '게시물 올리기'}
                        </button>
                    )}
                </div>
            </div>

            {/* 내 게시물 관리 (기공소 대표만) */}
            {isLabOwner && isSubscribed && showMyPosts && (
                <div style={S.myPostsCard}>
                    <LabPortfolioPosts user={user} />
                </div>
            )}

            {/* 스폰서 슬롯 (유료 예정 — 현재 무료 운영) */}
            {sponsors.length > 0 && (
                <div style={S.sponsorSection}>
                    <div style={S.sponsorRow}>
                        {sponsors.map(sp => (
                            <div key={sp.id} style={S.sponsorCard} onClick={() => navigate(`/lab/${sp.labId}`)}>
                                <div style={S.sponsorImgWrap}>
                                    {sp.photoUrl
                                        ? <img src={sp.photoUrl} alt="" style={S.sponsorImg} />
                                        : <div style={{ ...S.sponsorImg, background: '#f1f5f9', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#94a3b8' }}><Megaphone size={28} /></div>}
                                    <span style={S.adBadge}>광고</span>
                                </div>
                                <div style={S.sponsorName}>{sp.labName}</div>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {/* 카테고리 칩 */}
            <div style={S.chipRow}>
                {CATEGORIES.map(cat => (
                    <button key={cat} onClick={() => setCategory(cat)}
                        style={{
                            ...S.chip,
                            background: category === cat ? '#6366f1' : '#fff',
                            color: category === cat ? '#fff' : '#475569',
                            borderColor: category === cat ? '#6366f1' : '#e2e8f0',
                        }}>
                        {cat}
                    </button>
                ))}
            </div>

            {/* 인스타그램 그리드 */}
            {loading ? (
                <div style={S.loadingWrap}>
                    <div style={S.spinner} />
                    <p style={{ color: '#94a3b8' }}>갤러리를 불러오는 중...</p>
                </div>
            ) : posts.length === 0 ? (
                <div style={S.emptyState}>
                    <div style={{ fontSize: '48px', marginBottom: '16px' }}>&#128247;</div>
                    <h2 style={{ fontSize: '18px', fontWeight: '700', color: '#374151', marginBottom: '8px' }}>
                        {category === '전체' ? '아직 게시물이 없습니다' : `'${category}' 게시물이 없습니다`}
                    </h2>
                    <p style={{ color: '#94a3b8', fontSize: '14px' }}>기공소에서 작업물을 올리면 여기에 표시됩니다</p>
                </div>
            ) : (
                <>
                    <div style={S.grid}>
                        {posts.map(post => (
                            <div key={post.id} style={S.gridItem} onClick={() => openDetail(post)}>
                                <img src={post.photos?.[0]} alt="" style={S.gridImg} loading="lazy" />
                                {post.photos?.length > 1 && (
                                    <div style={S.gridMulti}><Layers size={14} color="#fff" /></div>
                                )}
                                <div style={S.gridOverlay}>
                                    <span style={S.gridLabName}>{post.labName}</span>
                                    <span style={S.gridLikes}>
                                        <Heart size={12} fill={likedPosts.has(post.id) ? '#ef4444' : 'none'} color={likedPosts.has(post.id) ? '#ef4444' : '#fff'} /> {post.likes || 0}
                                    </span>
                                </div>
                            </div>
                        ))}
                    </div>

                    {/* 무한 스크롤 센티널 + 더보기 */}
                    <div ref={sentinelRef} style={{ height: 1 }} />
                    {loadingMore && (
                        <div style={{ textAlign: 'center', padding: 24, color: '#94a3b8', fontSize: 14 }}>불러오는 중...</div>
                    )}
                    {!hasMore && posts.length > PAGE_SIZE - 1 && (
                        <div style={{ textAlign: 'center', padding: 24, color: '#cbd5e1', fontSize: 13 }}>모든 게시물을 확인했습니다</div>
                    )}
                </>
            )}

            {/* 게시물 상세 모달 */}
            {selectedPost && (
                <div style={S.modalOverlay} onClick={() => setSelectedPost(null)}>
                    <div style={S.modal} onClick={e => e.stopPropagation()}>
                        <button onClick={() => setSelectedPost(null)} style={S.modalClose}><X size={20} /></button>
                        <div style={S.modalLayout}>
                            {/* 사진 */}
                            <div style={S.modalImgSection}>
                                <img src={selectedPost.photos?.[currentPhotoIdx]} alt="" style={S.modalImg} />
                                {selectedPost.photos?.length > 1 && (
                                    <div style={S.modalDots}>
                                        {selectedPost.photos.map((_, i) => (
                                            <div
                                                key={i}
                                                onClick={() => setCurrentPhotoIdx(i)}
                                                style={{
                                                    ...S.modalDot,
                                                    background: i === currentPhotoIdx ? '#6366f1' : '#d1d5db',
                                                }}
                                            />
                                        ))}
                                    </div>
                                )}
                            </div>
                            {/* 정보 */}
                            <div style={S.modalInfo}>
                                <div style={S.modalHeader}>
                                    {selectedPost.labProfileImage ? (
                                        <img src={selectedPost.labProfileImage} alt="" style={{ ...S.modalAvatar, objectFit: 'cover' }} />
                                    ) : (
                                        <div style={S.modalAvatar}>{selectedPost.labName?.[0]}</div>
                                    )}
                                    <div>
                                        <div style={S.modalLabName} onClick={() => { setSelectedPost(null); navigate(`/portfolio/${selectedPost.labId}`); }}>
                                            {selectedPost.labName || '기공소'}
                                        </div>
                                        <div style={S.modalRegion}>{selectedPost.labRegion}</div>
                                    </div>
                                </div>
                                <div style={S.modalCategory}>{selectedPost.category}</div>
                                <p style={S.modalDesc}>{selectedPost.description}</p>
                                {selectedPost.tags?.length > 0 && (
                                    <div style={S.modalTags}>
                                        {selectedPost.tags.map((t, i) => <span key={i} style={S.modalTag}>#{t}</span>)}
                                    </div>
                                )}
                                <div style={S.modalActions}>
                                    <button onClick={(e) => toggleLike(selectedPost, e)} style={S.modalLikeBtn}>
                                        <Heart size={18} fill={likedPosts.has(selectedPost.id) ? '#ef4444' : 'none'} color={likedPosts.has(selectedPost.id) ? '#ef4444' : '#64748b'} />
                                        <span>{selectedPost.likes || 0}</span>
                                    </button>
                                    <span style={S.modalCommentCount}><MessageSquare size={16} color="#6366f1" /> {selectedPost.commentCount || 0}</span>
                                </div>

                                {/* 댓글 */}
                                <div style={S.commentsSection}>
                                    <div style={S.commentsTitle}>댓글</div>
                                    {auth.currentUser && (
                                        <div style={S.commentInput}>
                                            <input
                                                value={commentText}
                                                onChange={e => setCommentText(e.target.value)}
                                                onKeyDown={e => e.key === 'Enter' && submitComment()}
                                                placeholder="댓글을 입력하세요"
                                                style={S.commentInputField}
                                            />
                                            <button onClick={submitComment} style={S.commentSendBtn}><Send size={16} /></button>
                                        </div>
                                    )}
                                    <div style={S.commentsList}>
                                        {comments.map(c => (
                                            <div key={c.id} style={S.commentItem}>
                                                <div style={S.commentName}>{c.userName}</div>
                                                <div style={S.commentTextStyle}>{c.text}</div>
                                                <div style={S.commentTime}>{formatTime(c.createdAt)}</div>
                                            </div>
                                        ))}
                                        {comments.length === 0 && <p style={{ color: '#94a3b8', fontSize: '13px', textAlign: 'center', padding: '20px' }}>아직 댓글이 없습니다</p>}
                                    </div>
                                </div>

                                <button
                                    onClick={() => { setSelectedPost(null); navigate(`/portfolio/${selectedPost.labId}`); }}
                                    style={S.modalProfileBtn}
                                >
                                    포트폴리오 전체보기 <ChevronRight size={16} />
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

const S = {
    container: { maxWidth: '1100px', margin: '0 auto', padding: '24px 16px 60px', fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif' },
    loadingWrap: { display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '40vh', gap: '16px' },
    spinner: { width: '36px', height: '36px', border: '3px solid #e2e8f0', borderTopColor: '#6366f1', borderRadius: '50%', animation: 'spin 0.8s linear infinite' },
    emptyState: { textAlign: 'center', padding: '80px 20px' },

    pageHeader: { marginBottom: '20px' },
    pageTitle: { fontSize: '26px', fontWeight: '800', color: '#0f172a', margin: '0 0 4px' },
    pageSubtitle: { fontSize: '14px', color: '#64748b', margin: 0 },

    writeBtn: { display: 'flex', alignItems: 'center', gap: '6px', padding: '10px 20px', background: '#6366f1', color: '#fff', border: 'none', borderRadius: '10px', fontSize: '14px', fontWeight: '700', cursor: 'pointer', whiteSpace: 'nowrap', boxShadow: '0 2px 8px rgba(99,102,241,0.3)' },
    myPostsCard: { background: '#fff', border: '1px solid #e2e8f0', borderRadius: '16px', padding: '20px', marginBottom: '20px' },

    // 스폰서 슬롯
    sponsorSection: { marginBottom: '20px' },
    sponsorRow: { display: 'flex', gap: '12px', overflowX: 'auto', paddingBottom: '4px' },
    sponsorCard: { minWidth: '180px', maxWidth: '180px', cursor: 'pointer', flexShrink: 0 },
    sponsorImgWrap: { position: 'relative', height: '110px', borderRadius: '14px', overflow: 'hidden', border: '2px solid #fbbf24' },
    sponsorImg: { width: '100%', height: '100%', objectFit: 'cover' },
    adBadge: { position: 'absolute', top: '8px', left: '8px', background: 'rgba(15,23,42,0.75)', color: '#fbbf24', fontSize: '10px', fontWeight: '800', padding: '2px 8px', borderRadius: '8px', letterSpacing: '0.5px' },
    sponsorName: { fontSize: '13px', fontWeight: '700', color: '#0f172a', marginTop: '6px', textAlign: 'center', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },

    // 카테고리 칩
    chipRow: { display: 'flex', gap: '8px', overflowX: 'auto', paddingBottom: '6px', marginBottom: '16px', scrollbarWidth: 'none' },
    chip: { padding: '8px 16px', borderRadius: '20px', fontSize: '13.5px', fontWeight: '600', cursor: 'pointer', border: '1px solid #e2e8f0', whiteSpace: 'nowrap', flexShrink: 0, transition: 'all 0.15s' },

    // 인스타그램 그리드
    grid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(170px, 1fr))', gap: '6px' },
    gridItem: { position: 'relative', aspectRatio: '1', overflow: 'hidden', borderRadius: '6px', cursor: 'pointer', background: '#f1f5f9' },
    gridImg: { width: '100%', height: '100%', objectFit: 'cover', display: 'block', transition: 'transform 0.2s' },
    gridMulti: { position: 'absolute', top: '8px', right: '8px', background: 'rgba(0,0,0,0.5)', borderRadius: '8px', padding: '3px 6px', display: 'flex', alignItems: 'center' },
    gridOverlay: { position: 'absolute', bottom: 0, left: 0, right: 0, background: 'linear-gradient(transparent, rgba(0,0,0,0.65))', padding: '22px 10px 8px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' },
    gridLabName: { color: '#fff', fontSize: '12px', fontWeight: '700', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: '70%' },
    gridLikes: { color: '#fff', fontSize: '11px', fontWeight: '600', display: 'flex', alignItems: 'center', gap: '3px' },

    // Modal
    modalOverlay: { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 10000, padding: '20px' },
    modal: { background: '#fff', borderRadius: '20px', maxWidth: '900px', width: '100%', maxHeight: '90vh', overflow: 'hidden', position: 'relative' },
    modalClose: { position: 'absolute', top: '12px', right: '12px', background: 'rgba(255,255,255,0.9)', border: 'none', borderRadius: '50%', width: '36px', height: '36px', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', zIndex: 1 },
    modalLayout: { display: 'flex', height: '80vh', maxHeight: '600px' },
    modalImgSection: { flex: 1, background: '#000', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', position: 'relative' },
    modalImg: { maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' },
    modalDots: { position: 'absolute', bottom: '12px', display: 'flex', gap: '6px' },
    modalDot: { width: '8px', height: '8px', borderRadius: '50%', cursor: 'pointer' },
    modalInfo: { width: '320px', display: 'flex', flexDirection: 'column', overflowY: 'auto', padding: '20px', borderLeft: '1px solid #e2e8f0' },
    modalHeader: { display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '14px' },
    modalAvatar: { width: '40px', height: '40px', borderRadius: '50%', background: 'linear-gradient(135deg, #6366f1, #8b5cf6)', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '16px', fontWeight: '700', flexShrink: 0 },
    modalLabName: { fontSize: '15px', fontWeight: '700', color: '#0f172a', cursor: 'pointer' },
    modalRegion: { fontSize: '12px', color: '#6366f1' },
    modalCategory: { display: 'inline-block', padding: '3px 12px', background: '#f0f0ff', color: '#6366f1', borderRadius: '12px', fontSize: '12px', fontWeight: '600', marginBottom: '10px', alignSelf: 'flex-start' },
    modalDesc: { fontSize: '14px', color: '#374151', lineHeight: 1.7, marginBottom: '10px' },
    modalTags: { display: 'flex', flexWrap: 'wrap', gap: '4px', marginBottom: '12px' },
    modalTag: { fontSize: '12px', color: '#6366f1', fontWeight: '500' },
    modalActions: { display: 'flex', alignItems: 'center', gap: '16px', paddingBottom: '12px', borderBottom: '1px solid #f1f5f9', marginBottom: '12px' },
    modalLikeBtn: { display: 'flex', alignItems: 'center', gap: '6px', background: 'none', border: 'none', cursor: 'pointer', fontSize: '14px', fontWeight: '600', color: '#374151', padding: '6px 0' },
    modalCommentCount: { display: 'flex', alignItems: 'center', gap: '4px', fontSize: '14px', color: '#64748b' },
    modalProfileBtn: { display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px', width: '100%', padding: '12px', background: '#6366f1', color: '#fff', border: 'none', borderRadius: '10px', fontSize: '14px', fontWeight: '700', cursor: 'pointer', marginTop: 'auto' },

    // Comments
    commentsSection: { flex: 1, minHeight: 0 },
    commentsTitle: { fontSize: '13px', fontWeight: '700', color: '#374151', marginBottom: '8px' },
    commentInput: { display: 'flex', gap: '6px', marginBottom: '12px' },
    commentInputField: { flex: 1, padding: '8px 12px', border: '1px solid #e2e8f0', borderRadius: '8px', fontSize: '13px', outline: 'none' },
    commentSendBtn: { background: '#6366f1', color: '#fff', border: 'none', borderRadius: '8px', padding: '8px 12px', cursor: 'pointer', display: 'flex', alignItems: 'center' },
    commentsList: { maxHeight: '200px', overflowY: 'auto' },
    commentItem: { padding: '8px 0', borderBottom: '1px solid #f8fafc' },
    commentName: { fontSize: '12px', fontWeight: '700', color: '#0f172a' },
    commentTextStyle: { fontSize: '13px', color: '#475569', lineHeight: 1.5, marginTop: '2px' },
    commentTime: { fontSize: '10px', color: '#94a3b8', marginTop: '2px' },
};

export default LabFeed;
